<template>
  <ContentWrap>
    <el-form
      class="-mb-15px"
      :model="queryParams"
      ref="queryFormRef"
      :inline="true"
      label-width="80px"
    >
      <el-form-item label="品牌" prop="moduleCode">
        <el-select
          v-model="queryParams.moduleCode"
          filterable
          clearable
          placeholder="请选择品牌"
          class="!w-240px"
        >
          <el-option
            v-for="dict in getStrDictOptions(DICT_TYPE.SYSTEM_LOGIN_SKIN_BRAND)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="状态" prop="status">
        <el-select
          v-model="queryParams.status"
          placeholder="请选择状态"
          clearable
          class="!w-160px"
        >
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.SYSTEM_LOGIN_SKIN_STATUS)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon icon="ep:search" class="mr-5px" /> 搜索</el-button>
        <el-button @click="resetQuery"><Icon icon="ep:refresh" class="mr-5px" /> 重置</el-button>
        <el-button
          type="primary"
          plain
          v-hasPermi="['portal:login-skin:update']"
          @click="openForm('create')"
        >
          <Icon icon="ep:plus" class="mr-5px" /> 新增
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" :stripe="true" :show-overflow-tooltip="true">
      <el-table-column label="品牌" min-width="160">
        <template #default="{ row }">
          <div>{{ row.moduleTitle || getDictLabel(DICT_TYPE.SYSTEM_LOGIN_SKIN_BRAND, row.moduleCode) || row.moduleCode }}</div>
          <div class="text-12px text-[var(--el-text-color-secondary)]">{{ row.moduleCode }}</div>
        </template>
      </el-table-column>
      <el-table-column label="主标题" prop="title" min-width="120" />
      <el-table-column label="副标题" prop="subtitle" min-width="160" />
      <el-table-column label="Logo" width="80" align="center">
        <template #default="{ row }">
          <el-image
            v-if="row.logoUrl"
            :src="row.logoUrl"
            fit="contain"
            class="w-40px h-40px"
            :preview-src-list="[row.logoUrl]"
            preview-teleported
          />
          <span v-else class="text-[var(--el-text-color-placeholder)]">—</span>
        </template>
      </el-table-column>
      <el-table-column label="按钮色" width="100" align="center">
        <template #default="{ row }">
          <span
            v-if="row.buttonColor"
            class="inline-flex items-center gap-6px"
          >
            <i
              class="inline-block w-14px h-14px rounded-2px border border-[var(--el-border-color)]"
              :style="{ background: row.buttonColor }"
            />
            {{ row.buttonColor }}
          </span>
          <span v-else>—</span>
        </template>
      </el-table-column>
      <el-table-column label="绑定模块" min-width="180">
        <template #default="{ row }">
          <el-tag
            v-for="code in row.boundModuleCodes || []"
            :key="code"
            size="small"
            class="mr-4px mb-4px"
          >
            {{ moduleTitleMap[code] || code }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="90" align="center">
        <template #default="{ row }">
          <dict-tag :type="DICT_TYPE.SYSTEM_LOGIN_SKIN_STATUS" :value="row.status" />
        </template>
      </el-table-column>
      <el-table-column
        label="创建时间"
        prop="createTime"
        width="180"
        align="center"
        :formatter="dateFormatter"
      />
      <el-table-column label="操作" width="140" fixed="right" align="center">
        <template #default="{ row }">
          <el-button
            link
            type="primary"
            v-hasPermi="['portal:login-skin:update']"
            @click="openForm('update', row.id)"
          >
            编辑
          </el-button>
          <el-button
            link
            type="danger"
            v-hasPermi="['portal:login-skin:update']"
            @click="handleDelete(row.id)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      :total="total"
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      @pagination="getList"
    />
  </ContentWrap>

  <LoginSkinForm ref="formRef" :module-options="moduleOptions" @success="getList" />
</template>

<script lang="ts" setup>
import { DICT_TYPE, getDictLabel, getIntDictOptions, getStrDictOptions } from '@/utils/dict'
import { dateFormatter } from '@/utils/formatTime'
import { LoginSkinApi, type LoginSkinVO, type LoginSkinModuleOption } from '@/api/system/loginSkin'
import LoginSkinForm from './LoginSkinForm.vue'

defineOptions({ name: 'SystemLoginSkin' })

const message = useMessage()
const { t } = useI18n()

const loading = ref(true)
const total = ref(0)
const list = ref<LoginSkinVO[]>([])
const moduleOptions = ref<LoginSkinModuleOption[]>([])
const moduleTitleMap = computed(() => {
  const map: Record<string, string> = {}
  moduleOptions.value.forEach((item) => {
    map[item.code] = item.title
  })
  return map
})
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  moduleCode: undefined as string | undefined,
  status: undefined as number | undefined
})
const queryFormRef = ref()

const getList = async () => {
  loading.value = true
  try {
    const data = await LoginSkinApi.getPage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

const loadModuleOptions = async () => {
  moduleOptions.value = (await LoginSkinApi.getModuleOptions()) || []
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value?.resetFields()
  handleQuery()
}

const formRef = ref()
const openForm = (type: string, id?: number) => {
  formRef.value.open(type, id)
}

const handleDelete = async (id: number) => {
  try {
    await message.delConfirm()
    await LoginSkinApi.delete(id)
    message.success(t('common.delSuccess'))
    await getList()
  } catch {}
}

onMounted(async () => {
  await loadModuleOptions()
  await getList()
})
</script>
